import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Accordion } from "../ui/Accordion";
import useWindowWidthThreshold from "../hooks/useWindowWidthThreshold";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import type { CustomerPreOrder } from "../../../common/jsonRpcTypes";
import QuantityControls from "./QuantityControls";
import MenuItemCard from "../shared/MenuItemCard";
import AllergenWarning from "../shared/AllergenWarning";
import PricingSummary from "../shared/PricingSummary";
import ServiceChargeSelector from "./ServiceChargeSelector";
import "../../styles/basket.scss";
import PersonNameEditor from "./personNameEditor.tsx";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import {
  convertOptionsToSelectedOptions,
  getMenuMultiplier,
  getActualItemCount,
  getActualItemCountForMenu,
  findMenuItems,
  getAdditionalItemsForContainer,
  isItemSafeForPersonAllergies,
  resolveTypedQuantity,
  type MenuLimits,
} from "./preOrderUtils";
import { useItemHighlight } from "../hooks/useItemHighlight";
import { useVenueCurrency } from "../hooks/useVenueCurrency";
import { useInFlight } from "../hooks/useInFlight";
import { useLatchedTrue } from "../hooks/useLatchedTrue";
import ItemOptionsDisplay from "../shared/ItemOptionsDisplay";
import AlertBanner from "../shared/AlertBanner";
import { usePreorderContext } from "../context/PreorderContext";

const buildItemsByPerson = (
  items: CustomerPreOrder["items"],
  multipleNames: boolean,
  forNames: string[],
): { [key: string]: any[] } => {
  if (!multipleNames || !forNames || forNames.length === 0) {
    return { "0": items };
  }

  const itemsByPerson: { [key: string]: any[] } = {};
  for (let idx = 0; idx < forNames.length; idx++) {
    itemsByPerson[String(idx)] = [];
  }
  itemsByPerson.undefined = [];

  for (const it of items) {
    const key = it.for_name_index !== undefined && it.for_name_index !== null ? String(it.for_name_index) : "undefined";
    if (!itemsByPerson[key]) itemsByPerson[key] = [];
    itemsByPerson[key].push(it);
  }

  if (forNames.length >= 1 && itemsByPerson["0"] && itemsByPerson.undefined?.length) {
    const reassignedVisual = itemsByPerson.undefined.map((it) => ({ ...it, for_name_index: 0 }));
    itemsByPerson["0"] = [...itemsByPerson["0"], ...reassignedVisual];
    itemsByPerson.undefined = [];
  }

  return itemsByPerson;
};

// Build items by person for a specific booking type
const buildItemsByPersonForBookingType = (
  items: CustomerPreOrder["items"],
  multipleNames: boolean,
  forNames: string[],
): { [key: string]: any[] } => {
  return buildItemsByPerson(items, multipleNames, forNames);
};

const MOBILE_BASKET_KEY = "0";

interface BasketProps {
  bookingId: string;
  setForNames: React.Dispatch<React.SetStateAction<string[]>>;
  onSetOrderingFor?: (index: number) => void;
  onRemoveName?: (index: number) => void;
  /** Called with the full list once an edited name has been saved. */
  onNamesChanged?: (names: string[]) => void;
  menuLimits?: MenuLimits;
  onMaxItemsError?: (menuId: string, errorMessage: string) => void;
  totalCost?: number;
  totalCostWithService?: number;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  showServiceChargeSelector?: boolean;
  canChangeServiceCharge?: boolean;
  onServiceChargeChange?: (percentage: number) => Promise<boolean>;
  isCacheExpired?: boolean;
}

const Basket: React.FC<BasketProps> = ({
  bookingId,
  setForNames,
  onSetOrderingFor,
  onRemoveName,
  onNamesChanged,
  menuLimits = {},
  onMaxItemsError,
  totalCost,
  totalCostWithService,
  serviceChargeAmount = 0,
  serviceChargePercentage = 0,
  showServiceChargeSelector = false,
  canChangeServiceCharge = false,
  onServiceChargeChange,
  isCacheExpired = false,
}) => {
  const {
    customerPreOrder,
    customerPreOrders,
    bookingData,
    updateCustomerPreOrder,
    isItemPending,
    forNames,
    orderingFor,
    multipleNames,
    hidePricesByMenu,
    linkedBookings,
    bookingTypeId,
  } = usePreorderContext();
  const { t } = useTranslation();
  const nameSave = useInFlight();
  // Use customerPreOrders if available (multiple booking types), otherwise fall back to single customerPreOrder
  const cartItems = customerPreOrder?.items ?? [];
  const customerPreOrdersSafe = customerPreOrders ?? {};
  const hasMultipleBookingTypes = Object.keys(customerPreOrdersSafe).length > 1;

  // Helper function to get booking_type from menuId
  const getBookingTypeFromMenuId = useCallback(
    (menuId: string): string | undefined => {
      // For linked bookings, find the booking type from the linked booking structure
      if (linkedBookings) {
        for (const linkedBooking of linkedBookings) {
          const menuInLinkedBooking = linkedBooking.menus.find((m) => m.id === menuId);
          if (menuInLinkedBooking) {
            return linkedBooking.bookingTypeId;
          }
        }
      }

      // First, try to get the booking type from the menu's own booking_type property
      const menu = customerPreOrder?.menus?.find((m) => m.id === menuId);
      if (menu?.booking_type) {
        return menu.booking_type;
      }

      // Fallback to the bookingTypeId from the createBooking response
      // This is the authoritative source for the booking type in the /book flow
      if (bookingTypeId) {
        return bookingTypeId;
      }

      // Final fallback
      return undefined;
    },
    [linkedBookings, customerPreOrder?.menus, bookingTypeId],
  );

  // Group items by booking_type
  const itemsByBookingType = useCallback(() => {
    const groups: { [bookingType: string]: any[] } = {};

    for (const item of cartItems) {
      const bookingType = getBookingTypeFromMenuId(item.menu_id) ?? "default";
      if (!groups[bookingType]) {
        groups[bookingType] = [];
      }
      groups[bookingType].push(item);
    }

    return groups;
  }, [cartItems, getBookingTypeFromMenuId]);

  const isAccordionDisabled = useWindowWidthThreshold(1024);
  const [openAccordionKey, setOpenAccordionKey] = useState<string | null>(null);
  const isMobileBasketOpen = !isAccordionDisabled && openAccordionKey === MOBILE_BASKET_KEY;
  useBodyScrollLock(isMobileBasketOpen);
  const { currency, countryCode } = useVenueCurrency(bookingData?.venue);
  // Latched so a recalculation cannot withdraw the choice mid-session: refreshing the
  // pre-order (e.g. after removing a person) resolves the /book basket from the
  // pending-booking cache, which carries no required payment, and picking "None" can
  // drop the amount due to zero. Neither means the guest may no longer set the rate.
  const canChangeServiceChargeEver = useLatchedTrue(canChangeServiceCharge);
  const [basketSummary, setBasketSummary] = useState<{ totalItemsCount: number; itemsByPerson: any }>({
    totalItemsCount: 0,
    itemsByPerson: {},
  });
  // Track which unassigned items we've auto-assigned already (id+menu_id)
  const [autoAssignedIds, setAutoAssignedIds] = useState<Set<string>>(new Set());

  // Group items by booking type
  const groupedItems = itemsByBookingType();

  useEffect(() => {
    // When we have multiple booking types, aggregate items from all of them
    let items: any[] = [];
    if (hasMultipleBookingTypes) {
      for (const preOrder of Object.values(customerPreOrdersSafe)) {
        if (preOrder?.items) {
          items = [...items, ...preOrder.items];
        }
      }
    } else {
      items = cartItems ?? [];
    }

    const totalItemsCount = getActualItemCount(items);
    const itemsByPerson = buildItemsByPerson(items, multipleNames, forNames);
    setBasketSummary({ totalItemsCount, itemsByPerson });
  }, [cartItems, customerPreOrdersSafe, hasMultipleBookingTypes, forNames, multipleNames]);

  // Calculate aggregated totals when multiple booking types are present
  const aggregatedTotals = useMemo(() => {
    if (!hasMultipleBookingTypes) {
      return {
        totalCost,
        totalCostWithService,
        serviceChargeAmount,
      };
    }

    let aggregatedCost = 0;
    let aggregatedCostWithService = 0;
    let aggregatedServiceCharge = 0;

    for (const preOrder of Object.values(customerPreOrdersSafe)) {
      aggregatedCost += preOrder?.totalCost ?? 0;
      aggregatedCostWithService += preOrder?.totalCostWithService ?? 0;
      aggregatedServiceCharge += (preOrder?.totalCostWithService ?? 0) - (preOrder?.totalCost ?? 0);
    }

    return {
      totalCost: aggregatedCost,
      totalCostWithService: aggregatedCostWithService,
      serviceChargeAmount: aggregatedServiceCharge,
    };
  }, [hasMultipleBookingTypes, customerPreOrdersSafe, totalCost, totalCostWithService, serviceChargeAmount]);

  // Persist reassignment of any unassigned items whenever at least one name exists.
  useEffect(() => {
    if (!customerPreOrder || !multipleNames || forNames.length === 0) return;
    const unassigned = (cartItems ?? []).filter(
      (it: any) => it.quantity > 0 && (it.for_name_index === undefined || it.for_name_index === null),
    );
    if (unassigned.length === 0) return;

    const toAssign = unassigned.filter((it: any) => !autoAssignedIds.has(`${it.id}_${it.menu_id}`));
    if (toAssign.length === 0) return;

    setAutoAssignedIds((prev) => {
      const next = new Set(prev);
      for (const it of toAssign) {
        next.add(`${it.id}_${it.menu_id}`);
      }
      return next;
    });
    for (const it of toAssign) {
      try {
        updateCustomerPreOrder({
          booking_id: bookingId,
          pendingPreorderId: customerPreOrder?.pendingPreorderId ?? null,
          itemId: it.id,
          menuId: it.menu_id,
          amount: 0,
          multipleNames: true,
          forNames,
          orderingFor: 0,
          reassignExisting: true,
          booking_type: getBookingTypeFromMenuId(it.menu_id),
        });
      } catch (err) {
        console.error("Auto-assign name failed for item", it.id, err);
      }
    }
  }, [
    multipleNames,
    forNames,
    cartItems,
    autoAssignedIds,
    updateCustomerPreOrder,
    bookingId,
    customerPreOrder,
    getBookingTypeFromMenuId,
  ]);

  // State for editing a name
  const [editingNameIndex, setEditingNameIndex] = React.useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = React.useState("");
  const [nameSaveError, setNameSaveError] = React.useState<string | null>(null);

  const { highlightedItems, highlight: highlightItem } = useItemHighlight();
  const { highlightedItems: failedItems, highlight: flagFailedItem } = useItemHighlight();
  const [failedItemAnnouncement, setFailedItemAnnouncement] = useState(false);

  const updatePreOrder = async (
    targetId: string,
    targetMenuId: string,
    quantityDelta: number,
    itemForNameIndex?: number,
    itemOptions?: any[],
    itemName?: string,
    currentQuantity?: number,
  ) => {
    // The in-flight update already locked this row — a second tap must not send another request.
    if (isItemPending(targetId, targetMenuId, itemForNameIndex ?? orderingFor)) return;
    const itemKey = `${targetId}_${targetMenuId}_${itemForNameIndex ?? "undefined"}`;
    setFailedItemAnnouncement(false);

    // Use provided options if available, otherwise fall back to finding the item
    let selectedOptions: { [optionLabel: string]: { [valueId: string]: boolean } } | undefined;
    if (itemOptions && Array.isArray(itemOptions) && itemOptions.length > 0) {
      selectedOptions = convertOptionsToSelectedOptions(itemOptions);
    } else {
      const currentItem = (cartItems ?? []).find((it) => it.id === targetId && it.menu_id === targetMenuId);
      selectedOptions = currentItem?.options ? convertOptionsToSelectedOptions(currentItem.options) : undefined;
    }

    try {
      const updated = await updateCustomerPreOrder({
        booking_id: bookingId,
        pendingPreorderId: customerPreOrder?.pendingPreorderId ?? null,
        itemId: targetId,
        menuId: targetMenuId,
        amount: quantityDelta,
        selectedOptions,
        multipleNames,
        forNames,
        orderingFor: itemForNameIndex ?? orderingFor,
        booking_type: getBookingTypeFromMenuId(targetMenuId),
      });

      if (updated) {
        if (itemName && currentQuantity !== undefined) {
          highlightItem(itemKey);
        }
      } else {
        flagFailedItem(itemKey);
        setFailedItemAnnouncement(true);
      }
    } catch (error) {
      console.error("Failed to update preorder:", error);
      alert(t("update_preorder_error", "Failed to update item quantity. Please try again."));
    }
  };

  const handleMinus = async (
    id: string,
    menuId: string,
    currentQty: number,
    itemForNameIndex?: number,
    itemOptions?: any[],
    itemName?: string,
  ) => {
    if (currentQty > 0) {
      const multiplier = getMenuMultiplier(customerPreOrder ?? undefined, menuId);
      await updatePreOrder(id, menuId, -multiplier, itemForNameIndex, itemOptions, itemName, currentQty);
    }
  };

  const isMaxItemsReachedForMenu = (menuId: string): boolean => {
    const limits = menuLimits[menuId];
    if (!limits?.max) return false;

    // Use getActualItemCountForMenu to properly count nested items in fixed price menus
    const currentCount = getActualItemCountForMenu(cartItems, menuId);

    return currentCount >= limits.max;
  };

  const reportMaxItemsError = (menuId: string, currentCount: number) => {
    const limits = menuLimits[menuId];
    if (!onMaxItemsError || !limits?.max) return;

    const menuName =
      customerPreOrder?.menus?.find((m) => m.id === menuId)?.name ?? customerPreOrder?.menus?.[0]?.name ?? "";

    onMaxItemsError(
      menuId,
      `You have ${currentCount} items added to the ${menuName} menu. You can not add any more items.`,
    );
  };

  const handlePlus = async (
    id: string,
    menuId: string,
    currentQty: number,
    itemForNameIndex?: number,
    itemOptions?: any[],
    itemName?: string,
  ) => {
    // Check if max items reached for this menu
    if (isMaxItemsReachedForMenu(menuId)) {
      // Use getActualItemCountForMenu to properly count nested items in fixed price menus
      reportMaxItemsError(menuId, getActualItemCountForMenu(cartItems, menuId));
      return;
    }

    const multiplier = getMenuMultiplier(customerPreOrder ?? undefined, menuId);
    await updatePreOrder(id, menuId, multiplier, itemForNameIndex, itemOptions, itemName, currentQty);
  };

  // Commit a directly-typed quantity. The value is treated as the desired absolute
  // quantity and converted to the delta that updatePreOrder expects, honouring the
  // menu multiplier and the menu's max-items limit. An empty field or zero removes the item.
  const handleQuantityCommit = async (
    id: string,
    menuId: string,
    currentQty: number,
    itemForNameIndex?: number,
    itemOptions?: any[],
    itemName?: string,
    value?: number | null,
  ) => {
    const multiplier = getMenuMultiplier(customerPreOrder ?? undefined, menuId);
    let desired = resolveTypedQuantity(value ?? null, multiplier);

    if (desired === 0) {
      if (currentQty > 0) {
        await updatePreOrder(id, menuId, -currentQty, itemForNameIndex, itemOptions, itemName, currentQty);
      }
      return;
    }

    // Respect the menu max-items limit when increasing the quantity.
    if (desired > currentQty) {
      const limits = menuLimits[menuId];
      if (limits?.max) {
        const menuCount = getActualItemCountForMenu(cartItems, menuId);
        const headroom = Math.max(0, limits.max - menuCount);
        const maxDesired = currentQty + headroom;
        if (desired > maxDesired) {
          desired = maxDesired;
          // The typed quantity has been capped to the limit and the remaining
          // headroom is still added, so report the resulting count (the menu is
          // now at its maximum) rather than the pre-update count.
          reportMaxItemsError(menuId, menuCount + (desired - currentQty));
        }
      }
    }

    const delta = desired - currentQty;
    if (delta !== 0) {
      await updatePreOrder(id, menuId, delta, itemForNameIndex, itemOptions, itemName, currentQty);
    }
  };

  // Handler to start editing
  const handleEditName = (index: number) => {
    setNameSaveError(null);
    setEditingNameIndex(index);
    setEditingNameValue(forNames[index] || "");
  };

  // Handler to save edited name
  const handleSaveName = (index: number) =>
    nameSave.run(async () => {
      if (!editingNameValue.trim()) return;
      setNameSaveError(null);
      const newNames = [...forNames];
      newNames[index] = editingNameValue.trim();

      try {
        const saved = await updateCustomerPreOrder({
          booking_id: bookingId,
          pendingPreorderId: customerPreOrder?.pendingPreorderId ?? null,
          itemId: "",
          menuId: "",
          amount: 0,
          multipleNames: newNames.length > 0,
          forNames: newNames,
          orderingFor,
        });
        if (!saved) {
          setNameSaveError(t("name_save_failed", "Sorry, we couldn't save this name. Please try again."));
          return;
        }
      } catch (error) {
        console.error("Error syncing name edit to Redis:", error);
        setNameSaveError(t("name_save_failed", "Sorry, we couldn't save this name. Please try again."));
        return;
      }

      setForNames(newNames);
      setEditingNameIndex(null);
      setEditingNameValue("");
      onSetOrderingFor?.(index);
      onNamesChanged?.(newNames);
    });

  // Handler to remove a name - resets editing state before calling parent handler
  const handleRemoveNameWithReset = (index: number) => {
    setNameSaveError(null);
    setEditingNameIndex(null);
    setEditingNameValue("");
    onRemoveName?.(index);
  };

  const generateItemKey = (item: (typeof cartItems)[number], idx: number) => {
    const stringifyOptions = (obj: any) => {
      if (!obj || typeof obj !== "object") return String(obj);
      const sortedKeys = Object.keys(obj).sort((a, b) => a.localeCompare(b));
      const sortedObj: any = {};
      for (const key of sortedKeys) {
        sortedObj[key] = obj[key];
      }
      return JSON.stringify(sortedObj);
    };

    const idPart = item.id ?? "undefined";
    const menuPart = item.menu_id ?? "undefined";
    const optionsPart = stringifyOptions(item.options);
    const forNamePart = item.for_name_index ?? "undefined";

    return `${idPart}-${menuPart}-${optionsPart}-${forNamePart}-${idx}`;
  };
  const createMinusHandler =
    (
      itemId: string,
      menuId: string,
      quantity: number,
      forNameIndex: number | undefined,
      options?: any[],
      itemName?: string,
    ) =>
    () => {
      handleMinus(itemId, menuId, quantity, forNameIndex, options, itemName);
    };

  const createPlusHandler =
    (
      itemId: string,
      menuId: string,
      quantity: number,
      forNameIndex: number | undefined,
      options?: any[],
      itemName?: string,
    ) =>
    () => {
      handlePlus(itemId, menuId, quantity, forNameIndex, options, itemName);
    };

  const createQuantityCommitHandler =
    (
      itemId: string,
      menuId: string,
      quantity: number,
      forNameIndex: number | undefined,
      options?: any[],
      itemName?: string,
    ) =>
    (value: number | null) => {
      handleQuantityCommit(itemId, menuId, quantity, forNameIndex, options, itemName, value);
    };

  // Helper to render quantity controls (extracted to shared component)
  const renderQuantityControls = (
    itemId: string,
    menuId: string,
    quantity: number,
    forNameIndex: number | undefined,
    itemName: string | undefined,
    options?: any[],
  ) => {
    const isLoading = isItemPending(itemId, menuId, forNameIndex ?? orderingFor);
    const displayName = itemName ?? "";

    return (
      <QuantityControls
        variant="basket"
        quantity={quantity}
        itemName={displayName}
        isLoading={isLoading}
        onDecrease={createMinusHandler(itemId, menuId, quantity, forNameIndex, options, displayName)}
        onIncrease={createPlusHandler(itemId, menuId, quantity, forNameIndex, options, displayName)}
        onQuantityCommit={createQuantityCommitHandler(itemId, menuId, quantity, forNameIndex, options, displayName)}
      />
    );
  };

  const allergenMenus = useMemo(
    () => [...(customerPreOrder?.menus ?? []), ...(linkedBookings ?? []).flatMap((linked) => linked.menus)],
    [customerPreOrder?.menus, linkedBookings],
  );

  /** Flags a basket item chosen before its guest listed an allergen it contains. */
  const renderAllergenWarning = (
    itemId: string,
    menuId: string,
    lineAllergens: string[] | undefined,
    forNameIndex: number | undefined,
  ) => {
    const isOrderingForNames = multipleNames && forNames.length > 0;
    const personIndex = isOrderingForNames ? (forNameIndex ?? orderingFor) : 0;
    const sources = [...findMenuItems(allergenMenus, menuId, itemId), { allergens: lineAllergens }];
    const isSafe = sources.every((source) =>
      isItemSafeForPersonAllergies(source, personIndex, customerPreOrder?.dietaryRequirements),
    );
    if (isSafe) return null;
    return <AllergenWarning personName={isOrderingForNames ? forNames[personIndex] : undefined} />;
  };

  const renderNestedItem = (
    nestedItem: any,
    nestedIdx: number,
    menuId: string,
    forNameIndex: number | undefined,
    itemHidePrices: boolean,
  ) => {
    const nestedItemKey = `${nestedItem.id}_${menuId}_${forNameIndex ?? "undefined"}`;
    const isNestedHighlighted = highlightedItems.has(nestedItemKey);
    const isNestedFailed = failedItems.has(nestedItemKey);
    let nestedHighlightClass = "";
    if (isNestedFailed) {
      nestedHighlightClass = "basket-item-highlight--error";
    } else if (isNestedHighlighted) {
      nestedHighlightClass = "basket-item-highlight";
    }

    return (
      <li key={`${nestedItem.id}-${nestedIdx}`} style={{ marginBottom: "0.5rem" }} className={nestedHighlightClass}>
        <div className="menu-item-container">
          <span className="gcw:flex gcw:flex-col menu-item">{nestedItem.name}</span>
          {renderAllergenWarning(nestedItem.id, nestedItem.menu_id ?? menuId, nestedItem.allergens, forNameIndex)}
          {nestedItem.allergens?.length > 0 && (
            <span className="info-text dietary-text">
              {t("allergens_label", "Allergens: {{allergens}}", {
                allergens: nestedItem.allergens.join(", "),
              })}
            </span>
          )}
          <ItemOptionsDisplay
            options={nestedItem.options}
            currency={currency}
            countryCode={countryCode}
            hidePrices={itemHidePrices}
          />
          {!itemHidePrices && (
            <span className="menu-price-inline gcw:pt-1 gcw:pb-1">
              {formatCurrency(Number(nestedItem.price) || 0, currency, countryCode)}
            </span>
          )}
        </div>
        {renderQuantityControls(
          nestedItem.id,
          menuId,
          nestedItem.quantity,
          forNameIndex,
          nestedItem.name,
          nestedItem.options,
        )}
      </li>
    );
  };

  const renderItem = (item: (typeof cartItems)[number], idx: number, isDesktop: boolean = false) => {
    const itemHidePrices = hidePricesByMenu[item.menu_id] ?? false;

    if (item.type === "menu" && Array.isArray(item.items) && item.items.length > 0) {
      const effectiveMenuId = item.items[0]?.menu_id ?? item.menu_id;
      const menu = customerPreOrder?.menus?.find((m) => m.id === effectiveMenuId);
      const fixedPrice = item.total ?? menu?.fixed_price_rules?.[0]?.price;

      const additionalNeeded = getAdditionalItemsForContainer(item, customerPreOrder?.menus ?? []);

      return (
        <div key={generateItemKey(item, idx)} className="fixed-price-menu-container">
          <div className="fixed-price-menu-header gcw:flex gcw:justify-between gcw:items-center">
            <span className="fixed-price-menu-title">{menu?.name ?? item.name}</span>
            {!itemHidePrices && fixedPrice !== undefined && (
              <span className="fixed-price-menu-price">{formatCurrency(fixedPrice, currency, countryCode)}</span>
            )}
          </div>
          {additionalNeeded > 0 && (
            <AlertBanner className="gcw:rounded-md gcw:border gcw:bg-light-purple gcw:border-medium-grey gcw:mb-2 gcw:!p-0">
              <span>
                {t(
                  "fixed_price_add_more",
                  "This is a fixed price menu. Add {{count}} more item(s) for the same price!",
                  { count: additionalNeeded },
                )}
              </span>
            </AlertBanner>
          )}
          <div className="fixed-price-menu-items">
            <ul className="basket-list menu-list fixed-price-items-list">
              {item.items.map((nestedItem: any, nestedIdx: number) =>
                renderNestedItem(nestedItem, nestedIdx, item.menu_id, item.for_name_index, true),
              )}
            </ul>
          </div>
        </div>
      );
    }

    if (isDesktop) {
      const itemKey = `${item.id}_${item.menu_id}_${item.for_name_index ?? "undefined"}`;
      const isHighlighted = highlightedItems.has(itemKey);
      const isFailed = failedItems.has(itemKey);
      let highlightClass = "";
      if (isFailed) {
        highlightClass = " basket-item-highlight--error";
      } else if (isHighlighted) {
        highlightClass = " basket-item-highlight";
      }

      return (
        <li key={generateItemKey(item, idx)} className={`basket-item${highlightClass}`}>
          <div className="basket-item-content">
            <div className="gcw:flex gcw:justify-between gcw:items-start">
              <span className="basket-item-name">{item.name}</span>
              {!itemHidePrices && (
                <span className="basket-item-price">
                  {formatCurrency(Number(item.price) || 0, currency, countryCode)}
                </span>
              )}
            </div>
            <div className="basket-item-details">
              {renderAllergenWarning(item.id, item.menu_id, item.allergens, item.for_name_index)}
              {item.allergens && item.allergens.length > 0 && (
                <span className="info-text dietary-text">
                  {t("allergens_label", "Allergens: {{allergens}}", { allergens: item.allergens.join(", ") })}
                </span>
              )}
              <ItemOptionsDisplay
                options={item.options}
                currency={currency}
                countryCode={countryCode}
                hidePrices={itemHidePrices}
              />
              {Array.isArray(item.items) && item.items.length > 0 && (
                <div className="gcw:flex gcw:flex-col">
                  {item.items.map((opt, idx) => (
                    <div key={`${opt.id}-${idx}`}>
                      <strong className="options-label gcw:pt-2">{opt.name}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {renderQuantityControls(item.id, item.menu_id, item.quantity, item.for_name_index, item.name, item.options)}
          </div>
        </li>
      );
    }

    // Mobile view - also apply highlight effect
    const mobileItemKey = `${item.id}_${item.menu_id}_${item.for_name_index ?? "undefined"}`;
    const isMobileHighlighted = highlightedItems.has(mobileItemKey);
    const isMobileFailed = failedItems.has(mobileItemKey);
    let mobileHighlightClass = "";
    if (isMobileFailed) {
      mobileHighlightClass = "basket-item-highlight--error";
    } else if (isMobileHighlighted) {
      mobileHighlightClass = "basket-item-highlight";
    }

    return (
      <li key={generateItemKey(item, idx)} className={mobileHighlightClass}>
        <MenuItemCard
          variant="basket"
          name={item.name}
          price={Number(item.price) || 0}
          allergens={item.allergens}
          warning={renderAllergenWarning(item.id, item.menu_id, item.allergens, item.for_name_index)}
          options={item.options}
          nestedItems={Array.isArray(item.items) ? item.items : undefined}
          hidePrices={itemHidePrices}
          currency={currency}
          countryCode={countryCode}
          actions={renderQuantityControls(
            item.id,
            item.menu_id,
            item.quantity,
            item.for_name_index,
            item.name,
            item.options,
          )}
        />
      </li>
    );
  };

  // Helper to render person-grouped items or a flat item list for a booking type section
  const renderPersonOrItemList = (
    filteredItems: any[],
    itemsByPerson: Record<string, any[]>,
    names: string[],
    isDesktop: boolean,
  ) => {
    if (multipleNames && names.length > 0) {
      return names.map((personName: string, personIndex: number) => {
        const personItems = itemsByPerson[String(personIndex)] ?? [];
        const filteredPersonItems = personItems.filter((item: any) => item.quantity > 0);
        const personKey = `${personName}-${personIndex}`;

        return (
          <div key={personKey}>
            <PersonNameEditor
              personIndex={personIndex}
              personName={personName}
              isEditing={editingNameIndex === personIndex}
              editingValue={editingNameValue}
              isCurrentPerson={personIndex === orderingFor}
              isSaving={nameSave.isPending}
              saveError={editingNameIndex === personIndex ? (nameSaveError ?? undefined) : undefined}
              onEdit={handleEditName}
              onSave={handleSaveName}
              onRemove={handleRemoveNameWithReset}
              onSelect={onSetOrderingFor}
              onEditValueChange={setEditingNameValue}
            />
            {filteredPersonItems.length > 0 && (
              <ul className="basket-list menu-list">
                {filteredPersonItems.map((item: any, idx: number) => renderItem(item, idx, isDesktop))}
              </ul>
            )}
          </div>
        );
      });
    }
    return (
      <ul className="basket-list menu-list">
        {filteredItems.map((item: any, idx: number) => renderItem(item, idx, isDesktop))}
      </ul>
    );
  };

  // Helper to render basket content (items grouped by person or single list)
  const renderBasketContent = (isMobile: boolean) => {
    const isDesktop = !isMobile;

    // When multiple names mode is enabled and names exist, always show the PersonNameEditor
    // components even if there are no items yet so users can edit names before ordering.
    if (multipleNames && forNames.length > 0 && basketSummary.totalItemsCount === 0) {
      return forNames.map((personName, personIndex) => {
        const personKey = `${personName}-${personIndex}`;
        return (
          <PersonNameEditor
            key={personKey}
            personIndex={personIndex}
            personName={personName}
            isEditing={editingNameIndex === personIndex}
            editingValue={editingNameValue}
            isCurrentPerson={personIndex === orderingFor}
            isSaving={nameSave.isPending}
            saveError={editingNameIndex === personIndex ? (nameSaveError ?? undefined) : undefined}
            onEdit={handleEditName}
            onSave={handleSaveName}
            onRemove={handleRemoveNameWithReset}
            onSelect={onSetOrderingFor}
            onEditValueChange={setEditingNameValue}
          />
        );
      });
    }

    // When we have multiple booking types in customerPreOrders, use that directly
    if (hasMultipleBookingTypes) {
      const bookingTypes = Object.keys(customerPreOrdersSafe);

      if (bookingTypes.length === 0) {
        return <p className="empty-basket-message">{t("empty_basket_message", "There are no items in your basket")}</p>;
      }

      return bookingTypes.map((bookingType) => {
        const preOrder = customerPreOrdersSafe[bookingType];
        const items = preOrder?.items ?? [];
        const filteredItems = items.filter((item: any) => item.quantity > 0);

        if (filteredItems.length === 0) return null;

        const itemsByPerson = buildItemsByPersonForBookingType(
          filteredItems,
          multipleNames,
          preOrder?.forNames || forNames,
        );

        return (
          <div key={bookingType} className="booking-type-basket-section">
            {renderPersonOrItemList(filteredItems, itemsByPerson, preOrder?.forNames ?? forNames, isDesktop)}
          </div>
        );
      });
    } else {
      // Fallback to original behavior for single booking type
      const bookingTypes = Object.keys(groupedItems);

      if (bookingTypes.length === 0) {
        return <p className="empty-basket-message">{t("empty_basket_message", "There are no items in your basket")}</p>;
      }

      // Render separate basket sections for each booking type
      return bookingTypes.map((bookingType) => {
        const items = groupedItems[bookingType];
        const filteredItems = items.filter((item: any) => item.quantity > 0);

        if (filteredItems.length === 0) return null;

        const itemsByPerson = buildItemsByPersonForBookingType(filteredItems, multipleNames, forNames);

        return (
          <div key={bookingType} className="booking-type-basket-section">
            {renderPersonOrItemList(filteredItems, itemsByPerson, forNames, isDesktop)}
          </div>
        );
      });
    }
  };

  // Check if any item in the basket has hidden prices
  const allItemsForPriceCheck = useMemo(() => {
    if (hasMultipleBookingTypes) {
      let items: any[] = [];
      for (const preOrder of Object.values(customerPreOrdersSafe)) {
        if (preOrder?.items) {
          items = [...items, ...preOrder.items];
        }
      }
      return items;
    }
    return cartItems;
  }, [hasMultipleBookingTypes, customerPreOrdersSafe, cartItems]);

  if (!customerPreOrder && Object.keys(customerPreOrdersSafe).length === 0) {
    return null;
  }

  // Current person name for ordering (only shown in multi-person mode)
  const currentPersonName = multipleNames && forNames.length > 0 ? forNames[orderingFor] : null;

  const shouldHideTotal = allItemsForPriceCheck.some((item) => item.quantity > 0 && hidePricesByMenu[item.menu_id]);

  // Render the service charge selector when enabled (used in /book flow)
  const renderServiceChargeSelector = () => {
    if (!showServiceChargeSelector || shouldHideTotal) return null;

    return (
      <div className="service-charge-basket-section gcw:p-bs-3">
        <ServiceChargeSelector
          currentPercentage={serviceChargePercentage}
          currentAmount={serviceChargeAmount}
          onPercentageChange={onServiceChargeChange ?? (async () => true)}
          canChange={canChangeServiceChargeEver}
          currency={currency}
          countryCode={countryCode}
        />
      </div>
    );
  };

  // Render service charge and total for mobile accordion
  const renderMobilePricingSummary = () => {
    if (shouldHideTotal) return null;

    return (
      <PricingSummary
        variant="mobile"
        subtotal={aggregatedTotals.totalCost}
        totalWithService={aggregatedTotals.totalCostWithService}
        serviceChargeAmount={showServiceChargeSelector ? 0 : aggregatedTotals.serviceChargeAmount}
        serviceChargePercentage={showServiceChargeSelector ? 0 : serviceChargePercentage}
        currency={currency}
        countryCode={countryCode}
        taxInclusive={customerPreOrder?.tax_inclusive}
        taxBreakdown={customerPreOrder?.tax_breakdown}
        totalNet={customerPreOrder?.total_net}
        totalGross={customerPreOrder?.total_gross}
        taxLabel={customerPreOrder?.tax_label}
      />
    );
  };

  const cacheExpiredBanner = isCacheExpired ? (
    <AlertBanner variant="warning" className="gcw:m-4 widget-error-panel">
      {t("preorder_cache_expired_basket", "Your pre-order selection has expired. Please re-add your items.")}
    </AlertBanner>
  ) : null;

  return (
    <>
      {failedItemAnnouncement && (
        <div className="gcw:sr-only" role="alert">
          {t("basket_item_update_failed", "Sorry, we couldn't update this basket item. Please try again.")}
        </div>
      )}
      {isAccordionDisabled ? (
        <>
          <div id="gc-basket-header" className="basketHeader">
            <div className="gc-accordion-header">
              <div className="gcw:flex gcw:justify-between gcw:w-full">
                <span className="basket-title">{t("basket_header", "Basket")}</span>
                <span className="basket-items-no">
                  {basketSummary.totalItemsCount} {t(basketSummary.totalItemsCount === 1 ? "item" : "items")}
                </span>
              </div>
            </div>
            {currentPersonName && (
              <div className="ordering-for-text">
                {t("ordering_for", "Ordering for {{name}}", { name: currentPersonName })}
              </div>
            )}
          </div>

          {cacheExpiredBanner}
          <div id="gc-basket-items" className="basket-content-scrollable">
            {renderBasketContent(false)}
          </div>
          {renderServiceChargeSelector()}
        </>
      ) : (
        <>
          {cacheExpiredBanner}
          <Accordion
            activeKey={openAccordionKey}
            onSelect={(eventKey) => setOpenAccordionKey(typeof eventKey === "string" ? eventKey : null)}
          >
            <Accordion.Item eventKey={MOBILE_BASKET_KEY}>
              <Accordion.Header>
                <div id="gc-basket-header" className="gcw:flex gcw:flex-col gcw:w-full gcw:ps-bs-3 gcw:pe-bs-3">
                  <div className="gcw:flex gcw:justify-between gcw:w-full">
                    <span>{t("basket_header", "Basket")}</span>
                    <span>
                      {basketSummary.totalItemsCount} {t(basketSummary.totalItemsCount === 1 ? "item" : "items")}
                    </span>
                  </div>
                  {currentPersonName && (
                    <div className="ordering-for-text">
                      {t("ordering_for", "Ordering for {{name}}", { name: currentPersonName })}
                    </div>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div id="gc-basket-items">{renderBasketContent(true)}</div>

                {renderServiceChargeSelector()}
                {renderMobilePricingSummary()}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}
    </>
  );
};

export default Basket;
